/**
 * Admin 五合约池 — 查看/手工调池 + 自动流动/销毁 tick + OrbPoolLedger 链上公示同步
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { api, ApiError } from '../../api/client';

type Pools = {
  consensus_pool: string;
  fork_pool: string;
  burn_pool: string;
  seed_pool: string;
  exchange_reserve: string;
};

type LedgerSync = {
  id: number;
  tx_hash: string;
  consensus: string;
  fork: string;
  burn: string;
  seed: string;
  exchange_reserve: string;
  create_time: string;
  explorer_url: string;
};

type LedgerStatus = {
  address: string | null;
  verified: boolean;
  bscscan_url: string | null;
  owner_key_configured: boolean;
  onchain_pools: {
    consensus: string;
    fork: string;
    burn: string;
    seed: string;
    exchange_reserve: string;
  } | null;
  recent_syncs: LedgerSync[];
  last_sync: LedgerSync | null;
};

type StatusRes = {
  pools: Pools;
  flow_defaults: {
    consensus_to_fork_pct: string;
    fork_to_burn_pct: string;
    burn_pct: string;
  };
  note_zh: string;
  ledger?: LedgerStatus;
};

type TickRes = {
  dry_run: boolean;
  before: Pools;
  after: Pools;
  burned: string;
};

const POOL_FIELDS: Array<{ name: keyof Pools; label: string }> = [
  { name: 'consensus_pool', label: '共识池' },
  { name: 'fork_pool', label: '分叉池' },
  { name: 'burn_pool', label: '销毁池' },
  { name: 'seed_pool', label: '种子池' },
  { name: 'exchange_reserve', label: '兑换储备' },
];

export function ContractPoolsPage() {
  const [status, setStatus] = useState<StatusRes | null>(null);
  const [tick, setTick] = useState<TickRes | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingPools, setSavingPools] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form] = Form.useForm();
  const [metaForm] = Form.useForm();
  const [poolsForm] = Form.useForm<Pools>();

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await api.get<StatusRes>('/api/admin/contract-pools/status');
      setStatus(data);
      form.setFieldsValue({
        consensus_to_fork_pct: Number(data.flow_defaults.consensus_to_fork_pct),
        fork_to_burn_pct: Number(data.flow_defaults.fork_to_burn_pct),
        burn_pct: Number(data.flow_defaults.burn_pct),
        dry_run: true,
      });
      metaForm.setFieldsValue({
        address: data.ledger?.address ?? '',
        verified: data.ledger?.verified ?? false,
        bscscan_url: data.ledger?.bscscan_url ?? '',
      });
      poolsForm.setFieldsValue(data.pools);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }

  async function savePools(values: Pools) {
    Modal.confirm({
      title: '保存五池余额？',
      content: '将写入 php_uo_uo_config 最新配置行。不会自动上链；需要请再点「同步到链上」。',
      okText: '保存',
      onOk: async () => {
        setSavingPools(true);
        try {
          await api.patch<{ before: Pools; after: Pools }>('/api/admin/contract-pools/pools', {
            consensus_pool: String(values.consensus_pool),
            fork_pool: String(values.fork_pool),
            burn_pool: String(values.burn_pool),
            seed_pool: String(values.seed_pool),
            exchange_reserve: String(values.exchange_reserve),
          });
          message.success('池余额已保存');
          await load();
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : '保存失败');
        } finally {
          setSavingPools(false);
        }
      },
    });
  }

  async function runTick(values: {
    consensus_to_fork_pct: number;
    fork_to_burn_pct: number;
    burn_pct: number;
    dry_run: boolean;
  }) {
    setLoading(true);
    try {
      const data = await api.post<TickRes>('/api/admin/contract-pools/auto-tick', {
        consensus_to_fork_pct: String(values.consensus_to_fork_pct),
        fork_to_burn_pct: String(values.fork_to_burn_pct),
        burn_pct: String(values.burn_pct),
        dry_run: values.dry_run,
      });
      setTick(data);
      message.success(data.dry_run ? '干跑完成' : `已流动并销毁 ${data.burned}`);
      if (!data.dry_run) await load();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '执行失败');
    } finally {
      setLoading(false);
    }
  }

  function confirmSync() {
    Modal.confirm({
      title: '同步五池到链上？',
      content: '将把当前平台池余额（整数向下取整）写入 OrbPoolLedger。平台库不会被链上数据覆盖。',
      okText: '同步',
      onOk: async () => {
        setSyncing(true);
        try {
          const data = await api.post<{
            tx_hash: string;
            explorer_url: string;
          }>('/api/admin/contract-pools/ledger/sync', {});
          message.success(`已上链 ${data.tx_hash.slice(0, 10)}…`);
          window.open(data.explorer_url, '_blank');
          await load();
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : '同步失败');
        } finally {
          setSyncing(false);
        }
      },
    });
  }

  async function saveMeta(values: {
    address: string;
    verified: boolean;
    bscscan_url: string;
  }) {
    setLoading(true);
    try {
      await api.patch('/api/admin/contract-pools/ledger/meta', {
        address: values.address?.trim() || '',
        verified: values.verified,
        bscscan_url: values.bscscan_url?.trim() || null,
      });
      message.success('账本配置已保存');
      await load();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setLoading(false);
    }
  }

  const ledger = status?.ledger;

  return (
    <div>
      <Typography.Title level={4}>五合约池</Typography.Title>
      <Typography.Paragraph type="secondary">
        共识 / 分叉 / 销毁 / 种子 / 兑换储备。可手工改池余额，或触发自动流动 + 销毁（默认先干跑）。
        链上 OrbPoolLedger 为公示副本，改完平台池后需再点「同步到链上」。
      </Typography.Paragraph>

      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" loading={loading} onClick={() => void load()}>
          刷新池余额
        </Button>
      </Space>

      <Card title="平台池余额（可调整）" style={{ marginBottom: 16 }}>
        <Form form={poolsForm} layout="vertical" onFinish={(v) => void savePools(v)}>
          <Row gutter={16}>
            {POOL_FIELDS.map((f) => (
              <Col key={f.name} xs={24} sm={12} md={8} lg={4}>
                <Form.Item
                  name={f.name}
                  label={f.label}
                  rules={[
                    { required: true, message: `请输入${f.label}` },
                    {
                      validator: async (_, value) => {
                        const n = Number(value);
                        if (!Number.isFinite(n) || n < 0) {
                          throw new Error('须为非负数字');
                        }
                      },
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={1}
                    stringMode
                    controls={false}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
          <Space>
            <Button type="primary" htmlType="submit" loading={savingPools}>
              保存池余额
            </Button>
            <Button
              onClick={() => {
                if (status?.pools) poolsForm.setFieldsValue(status.pools);
              }}
            >
              重置为当前值
            </Button>
          </Space>
        </Form>
      </Card>

      <Card title="OrbPoolLedger 链上公示" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="合约地址">
              {ledger?.address ? (
                <Typography.Text copyable>{ledger.address}</Typography.Text>
              ) : (
                <Typography.Text type="secondary">未配置</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="验证状态">
              {ledger?.verified ? <Tag color="success">已验证</Tag> : <Tag>未验证</Tag>}
              {ledger?.bscscan_url && (
                <a href={ledger.bscscan_url} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>
                  BscScan
                </a>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Owner 私钥">
              {ledger?.owner_key_configured ? (
                <Tag color="blue">已配置（仅服务端 env）</Tag>
              ) : (
                <Tag color="warning">未配置 ORB_POOL_LEDGER_OWNER_PRIVATE_KEY</Tag>
              )}
            </Descriptions.Item>
            {ledger?.onchain_pools && (
              <Descriptions.Item label="链上当前值">
                共识 {ledger.onchain_pools.consensus} / 分叉 {ledger.onchain_pools.fork} / 销毁{' '}
                {ledger.onchain_pools.burn} / 种子 {ledger.onchain_pools.seed} / 储备{' '}
                {ledger.onchain_pools.exchange_reserve}
              </Descriptions.Item>
            )}
            {ledger?.last_sync && (
              <Descriptions.Item label="最近同步">
                {ledger.last_sync.create_time}{' '}
                <a href={ledger.last_sync.explorer_url} target="_blank" rel="noreferrer">
                  {ledger.last_sync.tx_hash.slice(0, 12)}…
                </a>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Button type="primary" loading={syncing} onClick={confirmSync} disabled={!ledger?.address}>
            同步到链上
          </Button>

          <Form form={metaForm} layout="vertical" onFinish={saveMeta} style={{ maxWidth: 560 }}>
            <Form.Item name="address" label="合约地址">
              <Input placeholder="0x…" />
            </Form.Item>
            <Form.Item name="bscscan_url" label="BscScan 链接">
              <Input placeholder="https://bscscan.com/address/0x…" />
            </Form.Item>
            <Form.Item name="verified" label="已验证" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Button htmlType="submit" loading={loading}>
              保存账本配置
            </Button>
          </Form>

          {ledger?.recent_syncs && ledger.recent_syncs.length > 0 && (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={ledger.recent_syncs}
              columns={[
                {
                  title: '时间',
                  dataIndex: 'create_time',
                  width: 180,
                },
                {
                  title: 'Tx',
                  dataIndex: 'tx_hash',
                  render: (h: string, row: LedgerSync) => (
                    <a href={row.explorer_url} target="_blank" rel="noreferrer">
                      {h.slice(0, 14)}…
                    </a>
                  ),
                },
                { title: '共识', dataIndex: 'consensus' },
                { title: '分叉', dataIndex: 'fork' },
                { title: '销毁', dataIndex: 'burn' },
                { title: '种子', dataIndex: 'seed' },
                { title: '储备', dataIndex: 'exchange_reserve' },
              ]}
            />
          )}
        </Space>
      </Card>

      <Card title="自动流动 / 销毁">
        <Form form={form} layout="inline" onFinish={runTick} initialValues={{ dry_run: true }}>
          <Form.Item name="consensus_to_fork_pct" label="共识→分叉 %">
            <InputNumber min={0} max={1} step={0.01} />
          </Form.Item>
          <Form.Item name="fork_to_burn_pct" label="分叉→销毁 %">
            <InputNumber min={0} max={1} step={0.01} />
          </Form.Item>
          <Form.Item name="burn_pct" label="销毁 %">
            <InputNumber min={0} max={1} step={0.01} />
          </Form.Item>
          <Form.Item name="dry_run" label="干跑" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              执行
            </Button>
          </Form.Item>
        </Form>
        {tick && (
          <Typography.Paragraph style={{ marginTop: 12 }}>
            {tick.dry_run ? '干跑' : '已执行'}：销毁量 {tick.burned}
          </Typography.Paragraph>
        )}
      </Card>
    </div>
  );
}
